import { useEffect, useState } from "react";

import { apis, callApi } from "../api";

export const MyOwnedCoursesStudentService = {
	listeners: {},
	savedCourses: {},

	addEventListener(slug, cb) {
		this.listeners[slug] = (this.listeners[slug] ?? []).filter(
			(listener) => listener !== cb,
		);
		this.listeners[slug].push(cb);
	},

	saveCourse(slug, course, passedLessons, upcomingLessons) {
		this.savedCourses[slug] = {
			course,
			passedLessons,
			upcomingLessons,
		};
		this.savedCourses = { ...this.savedCourses };
		this.emitChanges(slug);
	},

	finishALesson(slug, bookingId) {
		if (this.savedCourses[slug]) {
			const index = this.savedCourses[slug].upcomingLessons.findIndex(
				(booking) => booking.id === bookingId,
			);

			if (index === -1) {
				return;
			}

			const finishedLesson = this.savedCourses[slug].upcomingLessons[index];
			this.savedCourses[slug].upcomingLessons = this.savedCourses[
				slug
			].upcomingLessons.filter((booking) => booking.id !== bookingId);
			this.savedCourses[slug].passedLessons.unshift({
				...finishedLesson,
				isPassed: true,
			});
			this.savedCourses = { ...this.savedCourses };
			this.emitChanges(slug);
		}
	},

	clearData() {
		this.savedCourses = {};
		this.emitChanges();
	},

	emitChanges(slug) {
		for (const listener of this.listeners[slug] ?? []) {
			listener();
		}
	},

	removeEventListener(slug, cb) {
		if (this.listeners[slug]) {
			this.listeners[slug] = this.listeners[slug].filter(
				(listener) => listener !== cb,
			);
		}
	},
};

export function useMyOwnedCoursesStudentInit(slug) {
	const [isNotFound, setIsNotFound] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const getCourseBySlug = async () => {
			try {
				setIsLoading(true);
				setIsNotFound(false);
				const response = await callApi({
					method: "GET",
					url: `${apis.MY_OWNED_COURSE_BY_SLUG}/${slug}`,
				});

				if (!response) {
					return;
				}

				MyOwnedCoursesStudentService.saveCourse(
					slug,
					response.course,
					response.passedLessons,
					response.upcomingLessons,
				);
			} catch (error) {
				if (error?.response?.status === 404) {
					setIsNotFound(true);
				}
			} finally {
				setIsLoading(false);
			}
		};

		if (!slug) {
			return;
		}

		getCourseBySlug();
	}, [slug]);

	return { isNotFound, isLoading };
}

export function useMyOwnedCoursesStudent(slug) {
	const [course, setCourse] = useState(
		MyOwnedCoursesStudentService.savedCourses[slug]?.course ?? null,
	);
	const [passedLessons, setPassedLessons] = useState(
		MyOwnedCoursesStudentService.savedCourses[slug]?.passedLessons ?? [],
	);
	const [upcomingLessons, setUpcomingLessons] = useState(
		MyOwnedCoursesStudentService.savedCourses[slug]?.upcomingLessons ?? [],
	);

	useEffect(() => {
		const updateMyOwnedCourse = () => {
			setCourse(
				MyOwnedCoursesStudentService.savedCourses[slug]?.course ?? null,
			);
			setPassedLessons(
				MyOwnedCoursesStudentService.savedCourses[slug]?.passedLessons ?? [],
			);
			setUpcomingLessons(
				MyOwnedCoursesStudentService.savedCourses[slug]?.upcomingLessons ?? [],
			);
		};

		updateMyOwnedCourse();

		MyOwnedCoursesStudentService.addEventListener(slug, updateMyOwnedCourse);

		return () => {
			MyOwnedCoursesStudentService.removeEventListener(
				slug,
				updateMyOwnedCourse,
			);
		};
	}, [slug]);

	return {
		course,
		passedLessons,
		upcomingLessons,
	};
}
